// Opt-in reads only: a timed-out mutation may already have reached the server.
export const LOAD_RECOVERY_MS = 20000;
const ATTEMPT_TIMEOUT_MS = 8000;
const TRANSIENT_STATUS = new Set([0, 408, 425, 429, 500, 502, 503, 504, 600]);
const RETRY_DELAYS = [500, 1500, 3000, 5000];
const cancelledResult = () => ({ success: false, cancelled: true });

function pause(delay, signal) {
  return new Promise(resolve => {
    const finish = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', finish);
      resolve();
    };
    const timer = setTimeout(finish, delay);
    signal.addEventListener('abort', finish, { once: true });
    if (signal.aborted) finish();
  });
}

// One deadline covers the request, retries and backoff. It also settles a
// stalled transport that ignores cancellation; late responses cannot win.
export async function loadWithRecovery(load, { signal } = {}) {
  if (signal?.aborted) return cancelledResult();
  const controller = new AbortController();
  const started = Date.now();
  let lastFailure;
  let finish;
  const stopped = new Promise(resolve => { finish = resolve; });
  const stop = response => { finish(response); controller.abort(); };
  const abort = () => stop(cancelledResult());
  signal?.addEventListener('abort', abort, { once: true });
  const deadline = setTimeout(() => stop(lastFailure || { success: false, statusCode: 408 }), LOAD_RECOVERY_MS);
  const run = async () => {
    for (let attempt = 0; !controller.signal.aborted; attempt += 1) {
      const response = await load({ signal: controller.signal,
        timeout: Math.min(ATTEMPT_TIMEOUT_MS, Math.max(1, LOAD_RECOVERY_MS - (Date.now() - started))) });
      if (controller.signal.aborted || signal?.aborted) return cancelledResult();
      if (response?.success || response?.cancelled || !TRANSIENT_STATUS.has(response?.statusCode)) return response;
      lastFailure = response;
      // Retry-After is in seconds. Never hammer a rate-limited endpoint.
      const retryAfter = Number(response.retryAfter);
      const remaining = Math.max(0, LOAD_RECOVERY_MS - (Date.now() - started));
      const delay = Math.min(remaining, Math.max(RETRY_DELAYS[Math.min(attempt, RETRY_DELAYS.length - 1)],
        Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter * 1000 : 0));
      await pause(delay, controller.signal);
    }
    return cancelledResult();
  };
  try { return await Promise.race([stopped, run()]); }
  finally {
    clearTimeout(deadline);
    signal?.removeEventListener('abort', abort);
    controller.abort();
  }
}

// Adapter for fetch/Axios readers which throw instead of returning an envelope.
// Keep their public return type, error codes and authentication behavior.
export async function readWithRecovery(read, options) {
  const result = await loadWithRecovery(async request => {
    try { return { success: true, value: await read(request) }; }
    catch (error) {
      const status = error.status ?? error.response?.status;
      if (status === undefined && !(error instanceof TypeError) && error.name !== 'AbortError'
        && error.code !== 'ECONNABORTED' && error.code !== 'ERR_NETWORK') throw error;
      return { success: false, statusCode: status ?? 600, retryAfter: error.retryAfter, error };
    }
  }, options);
  if (result.success) return result.value;
  if (result.cancelled) throw new DOMException('Read cancelled', 'AbortError');
  if (result.error) throw result.error;
  throw Object.assign(new Error('LOAD_TIMEOUT'), { status: 408 });
}
